import { UnitsEnum } from '@/__generated__/graphql';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import {
  ALARM_LEVEL_MANUALLY_SET_TEMPLATE_KEY,
  ALARM_LEVEL_TEMPLATE_KEY,
  AlarmLevelSettingStatus,
  COVERAGE_PERCENTAGE,
  COVERAGE_RATING,
  MAX_BLISTER_HEIGHT_TEMPLATE_FIELD_KEY,
} from '@/constants';
import { Columns } from '@/types';
import { formatFieldValue, getCoverageLevel } from '@/utils';
import { useCallback } from 'react';
import { TableData } from '@/types/table';

export const useEquipmentData = () => {
  const unitSystem = useAtomValue(state.unitSystem);

  const getCellValue = useCallback(
    (assembly: TableData, column: Columns) => {
      const {
        templateFieldKey,
        unit,
        key,
        isTemplateField,
        optionsData,
        displayUnit,
        roundToDecimal,
        isBlisterField,
      } = column;
      const { templateFieldData } = assembly;
      let value;
      let subValue: string | undefined;
      if (!isTemplateField || !templateFieldKey || !templateFieldData) {
        value = (assembly as Record<string, unknown>)[key] as string;
        if (value === undefined || value === null || value === '')
          return { value: 'N/A', subValue };
        else return typeof value === 'object' ? { value: 'N/A', subValue } : { value, subValue };
      }
      value = templateFieldData?.[templateFieldKey];

      if (templateFieldKey === COVERAGE_RATING) {
        if (templateFieldData?.[COVERAGE_RATING] === undefined) {
          value = getCoverageLevel(templateFieldData?.[COVERAGE_PERCENTAGE], optionsData);
        }
      }

      // Handled for unexpected values (they exist in the database)

      if ([undefined, undefined, '', 'null', 'undefined'].includes(value))
        return { value: 'N/A', subValue };

      const isBlisterHeight = templateFieldKey === MAX_BLISTER_HEIGHT_TEMPLATE_FIELD_KEY;
      const isBooleanField = templateFieldKey === ALARM_LEVEL_MANUALLY_SET_TEMPLATE_KEY;
      if (isBlisterHeight && [0, '0'].includes(value)) return { value: 'Uncertain', subValue };
      if ([0, '0'].includes(value)) return { value: '0', subValue };

      value =
        formatFieldValue({
          unitSystem,
          type: undefined,
          unit: unit ? (unit as UnitsEnum) : undefined,
          value: value?.toString() ?? 'N/A',
          showUnitLabel: false,
          isBooleanField,
          displayUnit: displayUnit as UnitsEnum,
          roundToDecimal,
          isBlisterField,
          isHeight: isBlisterHeight,
        }) ?? value;

      // SubValue is only used for Alarm Level for now that is retrieved from another template field
      if (templateFieldKey === ALARM_LEVEL_TEMPLATE_KEY) {
        const isAlarmLevelManuallySet = templateFieldData?.[ALARM_LEVEL_MANUALLY_SET_TEMPLATE_KEY];
        if (isAlarmLevelManuallySet === true) subValue = AlarmLevelSettingStatus.MANUALLY_ADDED;
        else subValue = AlarmLevelSettingStatus.AUTO_GENERATED;
      }

      return { value, subValue };
    },
    [unitSystem]
  );
  return { getCellValue };
};
